'use client'

import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ArrowUpRight, Clock3, Play, X } from 'lucide-react'

type CreatorVideo = {
  id: string
  title: string
  summary: string
  creator: string
  handle: string
  channelUrl: string
  published: string
  duration: string
  thumbnailSize?: 'hq720' | 'hqdefault'
}

const creatorVideos: CreatorVideo[] = [
  {
    id: 'tnt39Uml9zE',
    title:
      'Public Service Announcement: NewChirp Software! New Version Of The CHIRP Radio Programming Software',
    summary:
      'NotaRubicon introduces NewChirp and explains what this reimagined programming tool brings to radio users.',
    creator: 'NotaRubicon Productions',
    handle: '@TheNotaRubicon',
    channelUrl: 'https://www.youtube.com/@TheNotaRubicon',
    published: 'Oct 22, 2026',
    duration: '3:45',
  },
  {
    id: 'xMxe0ct-o8Y',
    title: 'Testing Out NewChirp! Say Goodbye to Old Software?',
    summary:
      'A hands-on first look at NewChirp as a modern, cross-platform alternative to classic CHIRP.',
    creator: 'Joe · Amateur Radio and GMRS',
    handle: '@RFRadioNetwork',
    channelUrl: 'https://www.youtube.com/@RFRadioNetwork',
    published: 'Oct 24, 2026',
    duration: '7:42',
    thumbnailSize: 'hqdefault',
  },
  {
    id: 'BtQ1XhVy3v8',
    title: 'Ultimate NewChirp Update 1.0.20 Is Here! Plus Web Programmer!!??',
    summary:
      'Joe explores the 1.0.20 update and takes the browser-based Web Programmer for a spin.',
    creator: 'Joe · Amateur Radio and GMRS',
    handle: '@RFRadioNetwork',
    channelUrl: 'https://www.youtube.com/@RFRadioNetwork',
    published: 'Oct 26, 2026',
    duration: '6:23',
  },
  {
    id: 'eHqpnMetyM0',
    title: 'NewChirp Is Here! Is It Better Than the Original CHIRP?',
    summary:
      'PRK Radio takes a beginner-friendly look at NewChirp, programs a Baofeng UV-5R Mini, and asks whether it is worth switching from the original CHIRP.',
    creator: 'PRK Radio',
    handle: '@PRK_Radio',
    channelUrl: 'https://www.youtube.com/@PRK_Radio',
    published: 'Oct 27, 2026',
    duration: '9:39',
  },
]

function thumbnailUrl(id: string, size: 'hq720' | 'hqdefault' = 'hq720') {
  return `https://i.ytimg.com/vi/${id}/${size}.jpg`
}

export function CreatorVideos() {
  const [activeVideo, setActiveVideo] = useState<CreatorVideo | null>(null)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const closeButtonRef = useRef<HTMLButtonElement | null>(null)

  useEffect(() => {
    if (!activeVideo) return

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    closeButtonRef.current?.focus()

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setActiveVideo(null)
    }
    document.addEventListener('keydown', closeOnEscape)

    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener('keydown', closeOnEscape)
      triggerRef.current?.focus()
    }
  }, [activeVideo])

  const modal =
    activeVideo && typeof document !== 'undefined'
      ? createPortal(
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-background/85 p-4 backdrop-blur-sm"
            role="presentation"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) setActiveVideo(null)
            }}
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="creator-video-dialog-title"
              className="max-h-[calc(100vh-2rem)] w-full max-w-5xl overflow-y-auto rounded-2xl border border-border bg-card shadow-2xl"
            >
              <div className="flex items-center justify-between gap-4 border-b border-border px-4 py-3 sm:px-5">
                <div className="min-w-0">
                  <p className="font-mono text-xs text-primary">Now playing</p>
                  <h2
                    id="creator-video-dialog-title"
                    className="truncate text-sm font-semibold sm:text-base"
                  >
                    {activeVideo.title}
                  </h2>
                </div>
                <button
                  ref={closeButtonRef}
                  type="button"
                  onClick={() => setActiveVideo(null)}
                  aria-label="Close video"
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-border bg-background text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="aspect-video w-full bg-black">
                <iframe
                  src={`https://www.youtube-nocookie.com/embed/${activeVideo.id}?rel=0`}
                  title={activeVideo.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                  className="h-full w-full border-0"
                />
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-4 sm:px-5">
                <p className="text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">{activeVideo.creator}</span>
                  {' · '}
                  {activeVideo.handle}
                </p>
                <a
                  href={`https://www.youtube.com/watch?v=${activeVideo.id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
                >
                  Watch on YouTube
                  <ArrowUpRight className="h-4 w-4" />
                </a>
              </div>
            </div>
          </div>,
          document.body,
        )
      : null

  return (
    <section
      id="creator-videos"
      aria-labelledby="creator-videos-title"
      className="border-b border-border/60 bg-secondary/20 py-12 md:py-16"
    >
      <div className="mx-auto max-w-7xl px-4">
        <div className="flex flex-col justify-between gap-5 md:flex-row md:items-end">
          <div className="max-w-2xl">
            <span className="font-mono text-sm text-primary">// creators on YouTube</span>
            <h2
              id="creator-videos-title"
              className="mt-3 text-balance text-3xl font-bold tracking-tight sm:text-4xl"
            >
              See NewChirp through the radio community
            </h2>
            <p className="mt-4 text-pretty leading-relaxed text-muted-foreground">
              Independent creators have been putting NewChirp through its paces. Preview their
              videos here, then open a larger player without leaving the site.
            </p>
          </div>
          <p className="font-mono text-xs text-muted-foreground">
            Videos play through YouTube only after you choose one.
          </p>
        </div>

        <div className="mt-9 grid gap-5 md:grid-cols-2 xl:grid-cols-4">
          {creatorVideos.map((video) => (
            <article
              key={video.id}
              className="group overflow-hidden rounded-2xl border border-border bg-card shadow-sm transition-all hover:-translate-y-1 hover:border-primary/35 hover:shadow-xl hover:shadow-primary/5"
            >
              <button
                type="button"
                onClick={(event) => {
                  triggerRef.current = event.currentTarget
                  setActiveVideo(video)
                }}
                aria-label={`Play ${video.title} by ${video.creator}`}
                className="block w-full text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/50"
              >
                <span className="relative block aspect-video overflow-hidden bg-secondary">
                  <img
                    src={thumbnailUrl(video.id, video.thumbnailSize)}
                    alt={`${video.title} video thumbnail`}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                    onError={(event) => {
                      if (!event.currentTarget.src.endsWith('/hqdefault.jpg')) {
                        event.currentTarget.src = thumbnailUrl(video.id, 'hqdefault')
                      }
                    }}
                  />
                  <span
                    className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/5 to-transparent"
                    aria-hidden
                  />
                  <span className="absolute inset-0 flex items-center justify-center">
                    <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background/95 text-primary shadow-xl transition-transform duration-200 group-hover:scale-110">
                      <Play className="ml-0.5 h-6 w-6 fill-current" />
                    </span>
                  </span>
                  <span className="absolute bottom-3 right-3 rounded-md bg-black/80 px-2 py-1 font-mono text-xs text-white">
                    {video.duration}
                  </span>
                </span>

                <span className="block px-5 pb-2 pt-5">
                  <span className="block text-base font-semibold leading-snug transition-colors group-hover:text-primary">
                    {video.title}
                  </span>
                  <span className="mt-2 block text-sm leading-relaxed text-muted-foreground">
                    {video.summary}
                  </span>
                </span>
              </button>

              <div className="mx-5 flex flex-wrap items-center justify-between gap-3 border-t border-border/70 py-4">
                <a
                  href={video.channelUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="group/channel text-sm transition-colors hover:text-primary"
                >
                  <span className="block font-semibold">{video.creator}</span>
                  <span className="block font-mono text-xs text-muted-foreground">
                    {video.handle}
                  </span>
                </a>
                <span className="flex items-center gap-1.5 font-mono text-xs text-muted-foreground">
                  <Clock3 className="h-3.5 w-3.5" />
                  {video.published}
                </span>
              </div>
            </article>
          ))}
        </div>
      </div>

      {modal}
    </section>
  )
}
